import {
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';
import {
  disableBodyOverflow,
  enableBodyOverflow,
} from '../common/helpers/changeBodyClass';
import { Events } from '../components/wall/events';
import { EventKey } from '../common/enums/event-key';

export interface PopupConfig {
  inputs?: any;
  outputs?: { [key: string]: EventEmitter<any> };
  className?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PopupsManagerService {
  public container!: ViewContainerRef;
  public currentPopup?: ComponentRef<unknown> | null;
  public currentWrapper?: HTMLElement;

  public queue: any[] = [];

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

  static apply(target: any, object: any): void {
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        target[key] = object[key];
      }
    }
  }

  static createWrapper(additionalClassName?: string): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('class', `popup ${additionalClassName || ''}`);
    return element;
  }

  public initialize(container: ViewContainerRef): void {
    this.container = container;
  }

  public show<T>(component: Type<T>, config?: PopupConfig): ComponentRef<T> {
    const factory =
      this.componentFactoryResolver.resolveComponentFactory(component);
    const popup = this.container.createComponent<T>(
      factory,
      this.currentPopup ? this.queue.length + 1 : 0,
      this.container.injector
    );
    popup.location.nativeElement.parentNode.removeChild(
      popup.location.nativeElement
    );
    if (config?.inputs) {
      PopupsManagerService.apply(popup.instance, config.inputs);
    }
    if (config?.outputs) {
      PopupsManagerService.apply(popup.instance, config.outputs);
    }
    if (
      this.currentPopup &&
      !(this.currentPopup?.instance instanceof component)
    ) {
      this.queue.push(popup);
      return popup;
    }
    return this.attachPopup(popup, config?.className);
  }

  public hide(): void {
    Events.emit({ key: EventKey.HidePopup });
    this.currentPopup?.destroy();
    this.currentPopup = null;
    this.currentWrapper?.remove();
    enableBodyOverflow();
    this.checkInQueue();
  }

  private attachPopup<T>(
    popup: ComponentRef<T>,
    className?: string
  ): ComponentRef<T> {
    Events.emit({ key: EventKey.ShowPopup });
    disableBodyOverflow();
    this.currentPopup = popup;
    this.currentPopup.location.nativeElement.classList.add('popup__block');
    this.currentWrapper = PopupsManagerService.createWrapper(className);
    document.body.insertBefore(this.currentWrapper, document.body.firstChild);
    this.container.insert(popup.hostView);
    this.currentWrapper.appendChild(this.currentPopup.location.nativeElement);
    return this.currentPopup as ComponentRef<T>;
  }

  private checkInQueue(): void {
    const popup = this.queue.shift();
    if (popup) {
      this.attachPopup(popup);
    } else {
      this.container.clear();
    }
  }
}
